/**
 * US-100: the care-plan measure **source registry** — pure metadata describing
 * each `auto` measure source, plus the point-value extractor.
 *
 * No server deps. The registry declares each source's `kind` (point vs rate),
 * its selectable fields (for point sources), and the fixed rolling window (for
 * rate sources — a constant here, never a DB column or user input). The backend
 * `lib/carePlanProgress.ts` reads this to compute progress; the frontend reads
 * it to drive the measurement form dropdowns.
 */
import type { CarePlanMeasureSource } from "./enums";
import {
  encodeAssistanceLevel,
  encodeContinenceLevel,
} from "./adlTrendAnalysis";

/** How a point field's stored value maps to the numeric scale used by the bar. */
export type MeasureFieldEncode = "numeric" | "assistance" | "continence";

export interface MeasureFieldMeta {
  /** The DB column on `vital_signs` / `adl_records`. */
  field: string;
  label: string;
  unit: string;
  encode: MeasureFieldEncode;
}

export interface MeasureSourceMeta {
  kind: "point" | "rate";
  label: string;
  /** Default unit for the measure (point fields override per-field). */
  unit: string;
  /** Selectable fields — point sources only. */
  fields?: MeasureFieldMeta[];
  /** Fixed rolling window in days — rate sources only. */
  windowDays?: number;
  /** Rate shape — rate sources only. */
  rateUnit?: "percent" | "count";
}

/**
 * One entry per `CarePlanMeasureSource`. Point sources (vital, adl) carry the
 * latest reading toward a target; rate sources aggregate over `windowDays`
 * (computed backend-side by reusing the compliance analyzers / minimal counts).
 */
export const MEASURE_SOURCES: Record<CarePlanMeasureSource, MeasureSourceMeta> =
  {
    vital: {
      kind: "point",
      label: "Vital sign",
      unit: "",
      fields: [
        { field: "weight", label: "Weight", unit: "lbs", encode: "numeric" },
        {
          field: "systolic_bp",
          label: "Systolic BP",
          unit: "mmHg",
          encode: "numeric",
        },
        {
          field: "diastolic_bp",
          label: "Diastolic BP",
          unit: "mmHg",
          encode: "numeric",
        },
        {
          field: "heart_rate",
          label: "Heart rate",
          unit: "bpm",
          encode: "numeric",
        },
        {
          field: "respiratory_rate",
          label: "Respiratory rate",
          unit: "/min",
          encode: "numeric",
        },
        {
          field: "temperature",
          label: "Temperature",
          unit: "°F",
          encode: "numeric",
        },
        {
          field: "oxygen_saturation",
          label: "Oxygen saturation",
          unit: "%",
          encode: "numeric",
        },
        {
          field: "blood_sugar",
          label: "Blood sugar",
          unit: "mg/dL",
          encode: "numeric",
        },
      ],
    },
    adl: {
      kind: "point",
      label: "ADL",
      unit: "level",
      fields: [
        {
          field: "bathing",
          label: "Bathing assistance",
          unit: "level",
          encode: "assistance",
        },
        {
          field: "dressing",
          label: "Dressing assistance",
          unit: "level",
          encode: "assistance",
        },
        {
          field: "eating",
          label: "Eating assistance",
          unit: "level",
          encode: "assistance",
        },
        {
          field: "toileting",
          label: "Toileting assistance",
          unit: "level",
          encode: "assistance",
        },
        {
          field: "transferring",
          label: "Transferring assistance",
          unit: "level",
          encode: "assistance",
        },
        {
          field: "continence",
          label: "Continence",
          unit: "level",
          encode: "continence",
        },
        {
          field: "meal_intake_percent",
          label: "Meal intake",
          unit: "%",
          encode: "numeric",
        },
        {
          field: "fluid_intake_ml",
          label: "Fluid intake",
          unit: "ml",
          encode: "numeric",
        },
      ],
    },
    medication: {
      kind: "rate",
      label: "Medication adherence",
      unit: "%",
      windowDays: 30,
      rateUnit: "percent",
    },
    incident: {
      kind: "rate",
      label: "Incident frequency",
      unit: "/30d",
      windowDays: 30,
      rateUnit: "count",
    },
    appointment: {
      kind: "rate",
      label: "Appointment attendance",
      unit: "%",
      windowDays: 30,
      rateUnit: "percent",
    },
    bowel: {
      kind: "rate",
      label: "Bowel frequency",
      unit: "/7d",
      windowDays: 7,
      rateUnit: "count",
    },
  };

/** Look up a point field's metadata for a source (undefined if not a point field). */
export function getMeasureField(
  source: CarePlanMeasureSource,
  field: string,
): MeasureFieldMeta | undefined {
  return MEASURE_SOURCES[source].fields?.find((f) => f.field === field);
}

/**
 * Extract the numeric value of a point measure from a source row
 * (a `vital_signs` or `adl_records` row, loosely typed since this package is
 * Prisma-free). Numeric fields coerce with `Number()` (the pg adapter returns
 * Decimals as strings); categorical ADL fields use the US-086 ordinal encoders
 * (higher = more dependence). Returns `null` for missing / non-measurement
 * values (e.g. ADL `refused` / `not_performed`).
 */
export function extractPointValue(
  source: CarePlanMeasureSource,
  field: string,
  row: Record<string, unknown> | null | undefined,
): number | null {
  if (!row) return null;
  const meta = getMeasureField(source, field);
  if (!meta) return null;
  const raw = row[field];
  switch (meta.encode) {
    case "assistance":
      return encodeAssistanceLevel(raw == null ? null : String(raw));
    case "continence":
      return encodeContinenceLevel(raw == null ? null : String(raw));
    case "numeric": {
      if (raw == null) return null;
      const n = Number(raw);
      return Number.isNaN(n) ? null : n;
    }
  }
}
